import { Badge } from '@/components/UI';
import SummaryStat from './SummaryStat';
import { formatTimestamp, getAiIssueTriageRunBadgeVariant } from './helpers';
import type { AiIssueTriageRunSnapshot } from './types';

interface AiIssueTriageRunSectionProps {
    snapshot: AiIssueTriageRunSnapshot | undefined;
    isLoading: boolean;
    errorMessage: string | undefined;
}

const AiIssueTriageRunSection = ({ snapshot, isLoading, errorMessage }: AiIssueTriageRunSectionProps) => {
    if (isLoading) return <div className="text-sm text-zinc-500">Loading AI triage status...</div>;
    if (errorMessage) return <div className="rounded border border-red-900/70 bg-red-950/20 p-3 text-sm text-red-300">{errorMessage}</div>;
    if (!snapshot) return null;

    const { run, queue } = snapshot;
    const status = run?.status ?? 'not_started';
    const statusTone = getAiIssueTriageRunBadgeVariant(status);

    return (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
                <div className="font-medium text-white">AI Issue Triage Run</div>
                <Badge variant={statusTone}>{status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5 lg:grid-cols-10">
                <SummaryStat label="Open Now" tone={queue.open > 0 ? 'bad' : 'good'} value={queue.open} />
                <SummaryStat label="Selected" value={run?.issuesSelected ?? 0} />
                <SummaryStat label="Reviewed" value={run?.issuesReviewed ?? 0} />
                <SummaryStat label="Written" value={run?.reviewsWritten ?? 0} />
                <SummaryStat label="Updated" value={run?.issuesUpdated ?? 0} />
                <SummaryStat label="Codex" tone={(run?.codexQueuedCount ?? 0) > 0 ? 'bad' : 'neutral'} value={run?.codexQueuedCount ?? 0} />
                <SummaryStat label="Triaged" value={run?.triagedCount ?? 0} />
                <SummaryStat label="Dismissed" value={run?.dismissedCount ?? 0} />
                <SummaryStat
                    label="Failed"
                    tone={(run?.failedBatchCount ?? 0) > 0 ? 'bad' : 'neutral'}
                    value={run?.failedBatchCount ?? 0}
                />
                <div>
                    <div className="text-xs text-zinc-500">Heartbeat</div>
                    <div className="text-lg font-semibold text-white">{formatTimestamp(run?.lastHeartbeatAt)}</div>
                </div>
            </div>
            {run?.lastError && (
                <div className="mt-3 rounded border border-red-900/70 bg-red-950/20 p-2 text-sm text-red-300">{run.lastError}</div>
            )}
        </div>
    );
};

export default AiIssueTriageRunSection;
